import { NgTemplateOutlet } from '@angular/common';
import {
  Component,
  computed,
  contentChild,
  inject,
  input,
  InputSignal,
  linkedSignal,
  output,
  OutputEmitterRef,
  Signal,
  signal,
  TemplateRef,
  WritableSignal,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatDialogActions, MatDialogClose, MatDialogContent, MatDialogTitle } from '@angular/material/dialog';
import { MatError, MatFormField, MatHint, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { AiIllustrationComponent } from '../ai-illustration/ai-illustration.component';
import { ErrorMessagesComponent } from '../error-messages/error-messages.component';
import { SegmentedButtonComponent } from '../segmented-button/segmented-button.component';
import { ISegmentedButtonOption } from '../segmented-button/segmented-button.interface';
import {
  AI_ASSIST_ASSISTANT_TYPES,
  AI_ASSIST_PROMPT_MAX_LENGTH,
  AI_ASSIST_SOURCES,
  AI_CONTEXT_LEVEL_CAPTION_KEY,
  AI_CONTEXT_LEVEL_OPTIONS,
} from './ai-assist-shell.constants';
import {
  EAiAssistSource,
  EAiContextLevel,
  IAiAssistGenerateRequest,
  IAiAssistQuickAction,
  IAiAssistSourceConfig,
  TAiAssistQuickActionView,
} from './ai-assist-shell.interface';
import { isAiAssistPromptRequired, readAiContextLevel, saveAiContextLevel } from './ai-assist-shell.utilities';
import { AiQuickActionService } from './ai-quick-action.service';

/**
 * The body of every AI assist dialog: request form (quick actions, the user's own request, the context level), the AI
 * animation while the agent works, and the result frame with "Try again". Each dialog keeps its own store and projects
 * what differs: `<ng-template #aiAssistResult>` and `<ng-template #aiAssistResultActions>`. The request stays in the
 * form while the result is shown, so "Try again" comes back to it.
 */
@Component({
  imports: [
    AiIllustrationComponent,
    ErrorMessagesComponent,
    MatButton,
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogTitle,
    MatError,
    MatFormField,
    MatHint,
    MatIcon,
    MatInput,
    MatLabel,
    MatProgressSpinner,
    NgTemplateOutlet,
    ReactiveFormsModule,
    SegmentedButtonComponent,
    TranslateModule,
  ],
  selector: 'app-ai-assist-shell',
  styleUrl: './ai-assist-shell.component.scss',
  templateUrl: './ai-assist-shell.component.html',
})
export class AiAssistShellComponent {
  private readonly aiQuickActionService: AiQuickActionService = inject(AiQuickActionService);

  public readonly source: InputSignal<EAiAssistSource> = input.required<EAiAssistSource>();
  /**
   * Modes of one dialog, each its own source (e.g. edit and report in the item modal), shown as a switch above the
   * request; picking one emits `sourceChange` and the dialog passes it back as `source`. Empty: no switch.
   */
  public readonly modes: InputSignal<ISegmentedButtonOption<EAiAssistSource>[]> = input<
    ISegmentedButtonOption<EAiAssistSource>[]
  >([]);
  public readonly isLoading: InputSignal<boolean> = input<boolean>(false);
  public readonly hasResult: InputSignal<boolean> = input<boolean>(false);
  /** Quick actions the source cannot offer right now (e.g. "Fix description" while the description is empty). */
  public readonly disabledActions: InputSignal<readonly string[]> = input<readonly string[]>([]);
  /** The agent has nothing of the source to read (e.g. an item with no title and no description). */
  public readonly isSourceEmpty: InputSignal<boolean> = input<boolean>(false);
  /**
   * Context besides the source's own title and description (the items a new item's form has linked). It saves an empty
   * source from needing a request of its own, but only from level "standard" on, where the agent gets to read it.
   */
  public readonly hasLinkedContext: InputSignal<boolean> = input<boolean>(false);
  public readonly generate: OutputEmitterRef<IAiAssistGenerateRequest> = output<IAiAssistGenerateRequest>();
  public readonly tryAgain: OutputEmitterRef<void> = output<void>();
  public readonly sourceChange: OutputEmitterRef<EAiAssistSource> = output<EAiAssistSource>();

  public readonly resultTemplate: Signal<TemplateRef<unknown>> = contentChild.required('aiAssistResult', {
    read: TemplateRef,
  });
  public readonly resultActionsTemplate: Signal<TemplateRef<unknown>> = contentChild.required('aiAssistResultActions', {
    read: TemplateRef,
  });

  public readonly config: Signal<IAiAssistSourceConfig> = computed(() => AI_ASSIST_SOURCES[this.source()]);
  public readonly quickActions: Signal<TAiAssistQuickActionView[]> = computed(() =>
    this.aiQuickActionService.quickActionsByType()[AI_ASSIST_ASSISTANT_TYPES[this.source()]].map(
      (action: IAiAssistQuickAction): TAiAssistQuickActionView => ({
        ...action,
        isDisabled: this.disabledActions().includes(action.key),
      }),
    ),
  );
  public readonly isLoadingQuickActions: Signal<boolean> = this.aiQuickActionService.isLoading;
  /** The row marked as the default, else the first one; a source with no rows leaves only the user's own request. */
  private readonly defaultAction: Signal<string | null> = computed(() => {
    const actions: TAiAssistQuickActionView[] = this.quickActions().filter(
      (action: TAiAssistQuickActionView): boolean => !action.isDisabled,
    );

    return (actions.find((action: TAiAssistQuickActionView): boolean => action.isDefault) ?? actions[0])?.key ?? null;
  });
  /** Starts at the default action, again on every mode change; a second click on the selected one clears it. */
  public readonly selectedAction: WritableSignal<string | null> = linkedSignal(() => this.defaultAction());
  public readonly contextLevelOptions: ISegmentedButtonOption<EAiContextLevel>[] = AI_CONTEXT_LEVEL_OPTIONS;
  public readonly contextLevelCaptionKey: string = AI_CONTEXT_LEVEL_CAPTION_KEY;
  /** How much of the item goes to the agent; less context, fewer tokens. Remembered across dialogs. */
  public readonly contextLevel: WritableSignal<EAiContextLevel> = signal(readAiContextLevel());
  public readonly form = new FormGroup({
    prompt: new FormControl('', { nonNullable: true, validators: [Validators.maxLength(AI_ASSIST_PROMPT_MAX_LENGTH)] }),
  });
  /** Bound to the field's `required`: it marks the label and adds the validator, so "Generate" can stay enabled. */
  public readonly isPromptRequired: Signal<boolean> = computed(() =>
    isAiAssistPromptRequired({
      contextLevel: this.contextLevel(),
      hasLinkedContext: this.hasLinkedContext(),
      isSourceEmpty: this.isSourceEmpty(),
      selectedAction: this.selectedAction(),
    }),
  );

  constructor() {
    // The dialogs are the only place quick actions are shown, so the list is fetched when the first one opens.
    this.aiQuickActionService.load();
  }

  public onQuickActionClick(action: string): void {
    this.selectedAction.update((selected: string | null) => (selected === action ? null : action));
  }

  public onContextLevelChange(level: EAiContextLevel): void {
    this.contextLevel.set(level);
    saveAiContextLevel(level);
  }

  public onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();

      return;
    }

    this.generate.emit({
      action: this.selectedAction(),
      contextLevel: this.contextLevel(),
      prompt: this.form.controls.prompt.value.trim() || null,
    });
  }
}
